import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IonIcon } from "@ionic/react";
import { eyeOutline, eyeOffOutline } from "ionicons/icons";
import "./logincss/Login.css";
import logo from "../../images/auravit_logo.png";
import { auth } from "../../firebase";
import {
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { resetPasswordByEmail } from "../../services/adminService";
import { commonPasswords } from "../../services/passwordUtils";

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const fromForgot = location.state?.fromForgotPassword;
  const username = location.state?.username || "";

  const [policyError, setPolicyError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const coreValidations = {
    required: password.trim().length > 0,
    length: password.length >= 8 && password.length <= 24,
    uppercaseLowercase: /[A-Z]/.test(password) && /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[!@#$%^&*]/.test(password),
    notUsername: password.toLowerCase() !== username.toLowerCase(),
  };

  const errorValidations = {
    noSpaces: !/\s/.test(password),
    noCommon: !commonPasswords.includes(password),
    noRepeats: !/(.)\1\1\1/.test(password),
    confirmRequired: confirmPassword.trim().length > 0,
    match: password === confirmPassword && confirmPassword.length > 0,
  };

  const allValid =
    Object.values(coreValidations).every(Boolean) &&
    Object.values(errorValidations).every(Boolean);

  const passwordBorderStyle =
    password.length > 0 &&
    (!errorValidations.noSpaces ||
      !errorValidations.noCommon ||
      !errorValidations.noRepeats)
      ? "red"
      : "#ccc";
  const confirmBorderStyle =
    confirmPassword.length > 0 && !errorValidations.match ? "red" : "#ccc";

  const checkIfSameAsCurrentPassword = async () => {
    const normalizedEmail = String(email || "").trim().toLowerCase();
    if (!normalizedEmail) return false;

    try {
      await signInWithEmailAndPassword(auth, normalizedEmail, password);
      await signOut(auth);
      return true;
    } catch (err) {
      if (
        err?.code === "auth/invalid-credential" ||
        err?.code === "auth/wrong-password" ||
        err?.code === "auth/user-not-found" ||
        err?.code === "auth/invalid-login-credentials"
      ) {
        return false;
      }
      throw err;
    }
  };

  const handleResetPassword = async () => {
    if (isSubmitting) return; // Prevent duplicate submissions
    if (!coreValidations.required) {
      setPolicyError("New password is required.");
      return;
    }

    if (!errorValidations.confirmRequired) {
      setPolicyError("Confirm password is required.");
      return;
    }

    if (!allValid) {
      setPolicyError("Please complete all password requirements.");
      return;
    }

    if (password.toLowerCase() === username.toLowerCase()) {
      setPolicyError("Password cannot be same as your username.");
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const oobCode = params.get("oobCode");

    if (!fromForgot && !oobCode) {
      setPolicyError("Invalid or missing reset code.");
      setIsSubmitted(false);
      return;
    }

    setPolicyError("");
    setIsSubmitted(true);
    setIsSubmitting(true);

    try {
      const sameAsCurrentPassword = await checkIfSameAsCurrentPassword();
      if (sameAsCurrentPassword) {
        setPolicyError("New password must be different from your old password.");
        setIsSubmitted(false);
        return;
      }

      // SECURITY IMPROVEMENT: Unified password reset to use Cloud Function only
      // 
      // Previous implementation had dual-path password writes:
      // - Forgot path: resetPasswordByEmail Cloud Function
      // - Non-forgot path: Direct client-side Firestore updateDoc of password + accountSetup.password
      // 
      // Security concerns with client-side password writes:
      // - Client shouldn't write password hashes directly (security risk)
      // - Schema redundancy (both password and accountSetup.password fields)
      // - Inconsistent password handling between paths
      // 
      // Current implementation:
      // - Single code path via resetPasswordByEmail Cloud Function
      // - Cloud Function handles both Firebase Auth password reset and Firestore update
      // - Ensures consistent password field management (both root and nested)
      // - Removes security risk of client-side password hash writes
      await resetPasswordByEmail({ email, password, oobCode });
      navigate("/login");
    } catch (err) {
      console.error("Reset password error:", err);
      setPolicyError(err.message || "Unable to update password. Please try again.");
      setIsSubmitted(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    <div className="login-logo-container">
      <img
        src={logo}
        alt="AuraVit"
        className="login-logo"
        onClick={() => navigate("/")}
      />
    </div>
    <div className="forgot-container">
      <div className="forgot-card">
        <h1 className="forgot-title">Reset Your Password</h1>
        <p className="forgot-subtitle">
          {fromForgot
            ? "A reset link has been sent. Open the link and set your new password."
            : "Please Enter a new Password"}
        </p>

        <form className="forgot-form">
          <div className="input-group password-container">
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                placeholder="New Password"
                maxLength={24}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === " ") e.preventDefault();
                }}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                className={passwordBorderStyle === "red" ? "input-error" : ""}
              />
              <button
                type="button"
                className="eye-icon"
                onClick={() => setShowPassword(!showPassword)}
              >
                <IonIcon icon={showPassword ? eyeOutline : eyeOffOutline} />
              </button>
            </div>
            {!errorValidations.noSpaces && <div className="field-error">No spaces allowed</div>}
            {!errorValidations.noCommon && <div className="field-error">This is a common password</div>}
            {!errorValidations.noRepeats && (
              <div className="field-error">No more than 3 repeated characters in a row</div>
            )}
          </div>

          <div className="input-group password-container">
            <div className="password-input-wrapper">
              <input
                type={showConfirm ? "text" : "password"}
                placeholder="Confirm New Password"
                maxLength={24}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === " ") e.preventDefault();
                }}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                className={confirmBorderStyle === "red" ? "input-error" : ""}
              />

              <button
                type="button"
                className="eye-icon"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                <IonIcon icon={showConfirm ? eyeOutline : eyeOffOutline} />
              </button>
            </div>

            {policyError && (
              <div className="invalid-message">
                {policyError}
              </div>
            )}
            {!errorValidations.confirmRequired && isSubmitted && (
              <div className="field-error">Confirm password is required</div>
            )}
            {!errorValidations.match && confirmPassword.length > 0 && (
              <div className="field-error">Passwords must match</div>
            )}
          </div>

          <div className="password-rules">
            <RuleItem isValid={coreValidations.length} text="8-24 characters long" />
            <RuleItem
              isValid={coreValidations.uppercaseLowercase}
              text="Contains uppercase & lowercase letters"
            />
            <RuleItem isValid={coreValidations.number} text="Includes numbers (0-9)" />
            <RuleItem
              isValid={coreValidations.special}
              text="Includes special symbols (!@#$%^&*)"
            />
            <RuleItem isValid={coreValidations.notUsername} text="Does not match username" />
          </div>

          <button
            type="button"
            className="forgot-btn"
            disabled={!allValid || isSubmitted || isSubmitting}
            onClick={handleResetPassword}
          >
            {isSubmitted ? "Updating..." : "Done"}
          </button>
        </form>
      </div>
    </div>
    </>
  );
}

function RuleItem({ isValid, text }) {
  return (
    <div className="password-rule-item">
      <div
        className={`password-rule-checkbox ${isValid ? "valid" : ""}`}
      >
        {isValid && <span className="password-rule-checkmark">✓</span>}
      </div>
      <span className="password-rule-text">{text}</span>
    </div>
  );
}

